/* Loading the core and moving numbers across the boundary.
 *
 * The Rust core is compiled to a single self-contained WebAssembly module: no imports, no
 * emscripten runtime, no glue to keep in step — `WebAssembly.instantiate` and the exports below
 * are the whole loader.  This module is the only layer that knows about pointers; nothing under
 * `app/` does.  Heap views are re-read on every access because the module grows its memory on
 * demand, which detaches the old typed arrays.
 */

/** The flat C ABI, as exported by `rust/gcs-ffi`. */
export interface Abi {
  memory: WebAssembly.Memory;

  gcs_malloc(size: number): number;
  gcs_free(ptr: number, size: number): void;
  gcs_str_len(p: number): number;
  gcs_str_ptr(p: number): number;
  gcs_str_free(p: number): void;
  gcs_last_error(): number;

  gcs_registry_json(): number;
  gcs_word_between(ptr: number, len: number, a: number, b: number): number;
  gcs_version(): number;
  gcs_kernel_count(): number;

  gcs_sketch_new(): number;
  gcs_sketch_free(h: number): void;
  gcs_sketch_clone(h: number): number;
  gcs_sketch_from_json(p: number, n: number): number;
  gcs_sketch_to_json(h: number, indent: number): number;
  gcs_sketch_to_program(h: number): number;
  gcs_curve_polyline(h: number, idx: number, out: number, cap: number): number;
  gcs_envelope_sample(h: number, idx: number, u: number, v: number, roll: number, out: number): number;
  gcs_surface_domain(h: number, idx: number, out: number): number;
  gcs_surface_profile_bounds(h: number, idx: number, uMin: number, uMax: number, out: number): number;
  gcs_envelope_domain(h: number, idx: number, out: number): number;
  gcs_frame_sample(h: number, idx: number, angle: number, x: number, y: number, z: number, out: number): number;
  gcs_surface_project(h: number, idx: number, x: number, y: number, z: number, out: number): number;
  gcs_surface_sample(h: number, idx: number, u: number, v: number, out: number): number;
  gcs_counts_len(): number;
  gcs_program_elaborate(p: number, n: number): number;
  gcs_program_highlight(p: number, n: number): number;
  gcs_program_symbols(p: number, n: number): number;
  gcs_program_uses(p: number, n: number): number;
  gcs_module_set(np: number, nn: number, tp: number, tn: number): void;
  gcs_module_forget(): void;
  gcs_module_provided(np: number, nn: number): number;
  gcs_module_source(p: number, n: number): number;
  gcs_elab_free(h: number): void;
  gcs_elab_take_sketch(h: number): number;
  gcs_elab_text(h: number): number;
  gcs_elab_report(h: number): number;
  gcs_elab_stmt_span(h: number, stmt: number, out: number): number;
  gcs_elab_outline_json(h: number, s: number): number;
  gcs_elab_part_colours_json(h: number, s: number, p: number, n: number): number;
  gcs_elab_retext(h: number, p: number, n: number): number;
  gcs_elab_commit_seeds(h: number, sk: number): number;
  gcs_elab_describe(h: number, sk: number, id: number): number;
  gcs_elab_scenarios_json(h: number, sk: number): number;
  gcs_elab_scenarios_pictured_json(h: number, sk: number, drawn: number): number;
  gcs_elab_scenario_levels_new(h: number, sk: number, drawn: number): number;
  gcs_scenario_levels_next(l: number): number;
  gcs_scenario_levels_free(l: number): void;
  gcs_elab_scenario_marks_json(h: number, sk: number, k: number): number;
  gcs_elab_scenario_names_json(h: number): number;
  gcs_elab_measures_stated(h: number): number;
  gcs_elab_reconcile(h: number, sk: number): number;
  gcs_elab_add_use(h: number, name: number, len: number): number;
  gcs_elab_add_point(h: number, x: number, y: number): number;
  gcs_elab_add_rectangle(h: number, w: number, hh: number, plane: number, planeLen: number): number;
  gcs_elab_add_entity(h: number, p: number, n: number): number;
  gcs_elab_add_relation(h: number, p: number, n: number): number;
  gcs_elab_add_face(h: number, p: number, n: number): number;
  gcs_elab_add_solid(h: number, p: number, n: number): number;
  gcs_elab_add_body_word(h: number, p: number, n: number): number;
  gcs_elab_add_datum(h: number, s: number, p: number, n: number): number;
  gcs_elab_extrude(h: number, s: number, p: number, n: number): number;
  gcs_elab_set_sweep(h: number, solid: number, p: number, n: number): number;
  gcs_sketch_set_prism(h: number, i: number, way: number, d: number): number;
  gcs_elab_revolve(h: number, p: number, n: number): number;
  gcs_sketch_set_turn(h: number, i: number, degrees: number): number;
  gcs_elab_set_turn(h: number, solid: number, p: number, n: number): number;
  gcs_workspace_turn_handle_json(h: number, az: number, el: number, solid: number): number;
  gcs_workspace_turn_at(h: number, az: number, el: number, solid: number,
                        x: number, y: number, near: number): number;
  gcs_sketch_shape_epoch(h: number): number;
  gcs_workspace_extrude_handle_json(h: number, az: number, el: number, solid: number): number;
  gcs_workspace_extent_at(h: number, az: number, el: number, solid: number,
                          x: number, y: number): number;
  gcs_workspace_offset_handle_json(h: number, az: number, el: number, id: number): number;
  gcs_workspace_offset_at(h: number, az: number, el: number, id: number,
                          x: number, y: number): number;
  gcs_elab_sketch_on_face(h: number, s: number, solid: number, p: number, n: number): number;
  gcs_elab_face_plane_json(h: number, s: number, solid: number, p: number, n: number): number;
  gcs_elab_add_rounds(h: number, s: number, solid: number, p: number, n: number): number;
  gcs_elab_region_json(h: number, s: number, plane: number, x: number, y: number,
                       unit: number): number;
  gcs_elab_remove(h: number, sk: number, p: number, n: number): number;
  gcs_elab_make_component(h: number, sk: number, p: number, n: number): number;
  gcs_elab_set_dimension(h: number, cid: number, ap: number, an: number,
                         tp: number, tn: number): number;
  gcs_sketch_counts(h: number, out: number): void;
  gcs_sketch_point(h: number, x: number, y: number, fixed: number, name: number, nameLen: number): number;
  gcs_sketch_point_seen(h: number, az: number, el: number, x: number, y: number): number;
  gcs_sketch_line(h: number, p1: number, p2: number): number;
  gcs_sketch_set_hidden(h: number, p: number, n: number): number;
  gcs_sketch_set_shown_kinds(h: number, kinds: number, onParts: number, p: number,
                             n: number): number;
  gcs_sketch_unseen_json(h: number): number;
  gcs_sketch_set_cut(h: number, frame: number, flip: number, offset: number): number;
  gcs_sketch_cut(h: number, out: number): number;
  gcs_workspace_reach_along(h: number, frame: number, out: number): number;
  gcs_solid_section(h: number, idx: number, unit: number, out: number, cap: number): number;
  gcs_solid_shown_json(h: number): number;
  gcs_sketch_circle(h: number, center: number, r: number, name: number, nameLen: number): number;
  gcs_sketch_arc(h: number, c: number, s: number, e: number, name: number, nameLen: number): number;
  gcs_sketch_arc_through(h: number, s: number, e: number, tx: number, ty: number, name: number, nameLen: number): number;
  gcs_sketch_plane(h: number, ux: number, uy: number, uz: number, vx: number, vy: number,
                   vz: number, name: number, nameLen: number): number;
  gcs_frame_planar(h: number, idx: number): number;
  gcs_frame_moves(h: number, idx: number): number;
  gcs_plane_basis(h: number, idx: number, out: number): number;
  gcs_plane_frame3(h: number, idx: number, out: number): number;
  gcs_plane_glyph(h: number, idx: number, unit: number, out: number): number;
  gcs_point_plane(h: number, idx: number): number;
  gcs_point_set_plane(h: number, idx: number, plane: number): number;
  gcs_sketch_spline(h: number, ctrl: number, n: number): number;
  gcs_sketch_spline_knots(h: number, ctrl: number, n: number, knots: number, nk: number): number;
  gcs_sketch_spline_through(h: number, pts: number, n: number, hold: number): number;
  gcs_sketch_rectangle(h: number, a: number, x1: number, y1: number, name: number, nameLen: number, out: number): void;
  gcs_sketch_get_x(h: number, out: number): void;
  gcs_sketch_set_x(h: number, x: number, n: number): number;
  gcs_sketch_meshed_at(h: number, out: number): number;
  gcs_sketch_mesh_at(h: number, x: number, n: number): number;
  gcs_sketch_perturb(h: number, sigma: number, seed: number): void;
  gcs_sketch_topology_key(h: number): number;
  gcs_sketch_extent(h: number): number;
  gcs_sketch_bounds(h: number, drawn: number, out: number): void;
  gcs_sketch_nearest_point(h: number, x: number, y: number, outDist: number): number;
  gcs_sketch_pick(h: number, x: number, y: number, tol: number, out: number): number;
  gcs_sketch_n_residuals(h: number): number;
  gcs_sketch_set_constraints(h: number, p: number, n: number): void;

  gcs_param_value(h: number, i: number): number;
  gcs_param_set_value(h: number, i: number, v: number): void;
  gcs_param_fixed(h: number, i: number): number;
  gcs_param_set_fixed(h: number, i: number, v: number): void;
  gcs_param_name(h: number, i: number): number;

  gcs_entity_params(h: number, kind: number, idx: number, out: number): number;
  gcs_entity_points(h: number, kind: number, idx: number, out: number): number;
  gcs_entity_radius_param(h: number, kind: number, idx: number): number;
  gcs_entity_class(h: number, kind: number, idx: number): number;
  gcs_entity_set_class(
    h: number, kind: number, idx: number, name: number, nameLen: number, on: number): void;
  gcs_entity_style(h: number, kind: number, idx: number): number;
  gcs_styles_json(h: number): number;
  gcs_sketch_svg(h: number, width: number): number;
  gcs_sketch_preview(h: number, plane: number): void;
  gcs_drawing_svg(ptr: number, len: number): number;
  gcs_drawing_info(ptr: number, len: number): number;
  gcs_drawing_highlight(ptr: number, len: number): number;
  gcs_style_named(h: number, name: number, len: number): number;
  gcs_style_epoch(h: number): number;
  gcs_entity_bounds(h: number, kind: number, idx: number, out: number): void;
  gcs_entity_name(kind: number, idx: number): number;
  gcs_distance_between(h: number, ka: number, ia: number, kb: number, ib: number): number;
  gcs_signed_point_to_line(h: number, x: number, y: number, line: number): number;
  gcs_angle_between(h: number, a: number, b: number): number;
  gcs_dimension_between_lines(h: number, a: number, b: number): number;
  gcs_on_radius(cx: number, cy: number, tx: number, ty: number, r: number,
                out: number): number;
  gcs_arc_angles(h: number, idx: number, out: number): void;
  gcs_spline_knots(h: number, idx: number, out: number): number;
  gcs_spline_domain(h: number, idx: number, out: number): void;
  gcs_spline_eval(h: number, idx: number, t: number, out: number): void;
  gcs_spline_polyline(h: number, idx: number, unit: number, out: number, cap: number): number;
  gcs_spline_closest(h: number, idx: number, x: number, y: number, out: number): void;
  gcs_spline_insert_control(h: number, idx: number, t: number): number;
  gcs_three_point_arc(ax: number, ay: number, bx: number, by: number, cx: number, cy: number, out: number): number;

  gcs_constraint_add(h: number, p: number, n: number): number;
  gcs_constraint_remove(h: number, id: number): void;
  gcs_constraints_json(h: number): number;
  gcs_constraint_json(h: number, id: number): number;
  gcs_constraint_set_num(h: number, id: number, name: number, nameLen: number,
                         v: number): number;
  gcs_constraint_set_str(h: number, id: number, name: number, nameLen: number,
                         v: number, vLen: number): number;
  gcs_constraint_set_dimension(h: number, id: number, name: number, nameLen: number,
                               text: number, textLen: number): number;
  gcs_exprs_json(h: number): number;
  gcs_constraint_set_target(h: number, id: number, tx: number, ty: number): void;
  gcs_constraint_error(h: number, id: number): number;
  gcs_constraint_params(h: number, id: number, out: number): number;
  gcs_constraint_widths(h: number, id: number, out: number): number;
  gcs_constraint_local_values(h: number, id: number, out: number): number;
  gcs_constraint_eval(h: number, id: number, v: number, r: number, j: number): number;
  gcs_same_constraint(h: number, a: number, aLen: number, b: number,
                      bLen: number): number;
  gcs_constraint_duplicate(h: number, p: number, n: number): number;
  gcs_constraint_stating(h: number, p: number, n: number): number;
  gcs_describe(h: number, id: number): number;
  gcs_overview_json(h: number, unit: number, az: number, el: number, shaded: number): number;
  gcs_overview3d_json(h: number, unit: number, layer: number): number;
  gcs_callouts_json(h: number, unit: number): number;
  gcs_selected_callouts_json(h: number, unit: number, ids: number, count: number): number;
  gcs_derived_json(h: number, unit: number): number;
  gcs_derived_inputs(h: number, out: number, cap: number): number;
  gcs_solid_mesh(h: number, idx: number, unit: number, out: number, cap: number): number;
  gcs_solid_normals(h: number, idx: number, unit: number, out: number, cap: number): number;
  gcs_solid_faces_json(h: number, idx: number, unit: number): number;
  gcs_solid_drawn(h: number, idx: number, unit: number): number;
  gcs_solid_placement(h: number, idx: number, unit: number, built: number, out: number,
                      cap: number): number;
  gcs_solid_preview_json(h: number, p: number, n: number): number;
  gcs_sketch_defer_fields(h: number, on: number): void;
  gcs_sketch_field_jobs(h: number): number;
  gcs_solid_supply_field(h: number, idx: number, vertices: number, nv: number, triangles: number,
    nt: number, provisional: number): number;
  gcs_solid_stale(h: number, idx: number): number;
  gcs_field_mesher_new(h: number, idx: number, fineness: number): number;
  gcs_field_mesher_step(m: number, budget: number): number;
  gcs_field_mesher_progress(m: number): number;
  gcs_field_mesher_snapshot(m: number, out: number): number;
  gcs_field_mesher_vertices(m: number, out: number, cap: number): number;
  gcs_field_mesher_triangles(m: number, out: number, cap: number): number;
  gcs_field_mesher_free(m: number): void;
  gcs_derived_detail_free(h: number): number;
  gcs_exact_builder_new(h: number, idx: number): number;
  gcs_exact_builder_step(b: number): number;
  gcs_exact_builder_progress(b: number): number;
  gcs_exact_builder_display(b: number): number;
  gcs_sketch_supply_objects(h: number, on: number): void;
  gcs_sketch_object_jobs(h: number): number;
  gcs_sketch_supply_object(h: number, idx: number, ptr: number, len: number): number;
  gcs_sketch_refuse_object(h: number, idx: number, ptr: number, len: number): number;
  gcs_sketch_object_states(h: number): number;
  gcs_exact_builder_free(b: number): void;
  gcs_sketch_solid_claims_json(h: number): number;
  gcs_claim_judge_new(h: number): number;
  gcs_claim_judge_step(j: number): number;
  gcs_claim_judge_progress(j: number): number;
  gcs_claim_judge_verdicts_json(j: number): number;
  gcs_claim_judge_free(j: number): void;
  gcs_solid_glb(h: number, idx: number, unit: number): number;
  gcs_solid_objects_json(h: number): number;
  gcs_solid_unbuilt_json(h: number): number;
  gcs_materials_json(h: number): number;
  gcs_solid_stl(h: number, idx: number, unit: number): number;
  gcs_solid_exact(h: number, idx: number, kind: number, tolerance: number): number;
  gcs_workspace_json(h: number): number;
  gcs_workspace_eye(az: number, el: number, out: number): number;
  gcs_workspace_maps(h: number, az: number, el: number, out: number, cap: number): number;
  gcs_workspace_space_points(h: number, az: number, el: number, out: number, cap: number): number;
  gcs_workspace_bounds(h: number, unit: number, az: number, el: number, out: number): number;
  gcs_workspace_bounds_of(h: number, unit: number, az: number, el: number, p: number, n: number,
                          out: number): number;
  gcs_workspace_pick(h: number, unit: number, az: number, el: number, x: number, y: number,
                     tol: number, out: number): number;
  gcs_workspace_panes_at(h: number, az: number, el: number, x: number, y: number,
                         out: number, cap: number): number;
  gcs_workspace_pick_solid_json(h: number, az: number, el: number, x: number, y: number): number;
  gcs_workspace_pick_edge_json(h: number, az: number, el: number, x: number, y: number,
                               tol: number): number;
  gcs_workspace_solid_edges_json(h: number, solid: number): number;
  gcs_workspace_under_json(h: number, unit: number, az: number, el: number, x: number,
                           y: number, tol: number): number;
  gcs_workspace_round_size(h: number, solid: number): number;
  gcs_workspace_nearest_point(h: number, az: number, el: number, x: number, y: number,
                              outDist: number): number;
  gcs_workspace_overlapping_json(h: number, unit: number, az: number, el: number,
                                 x0: number, y0: number, x1: number, y1: number): number;
  gcs_workspace_callout_pick(h: number, unit: number, az: number, el: number,
                             x: number, y: number, tolPx: number, outView: number): number;
  gcs_callout_grab(h: number, id: number, unit: number, x: number, y: number,
                   out: number): number;
  gcs_callout_drag(h: number, id: number, x: number, y: number, gu: number, gv: number): number;
  gcs_callout_reset(h: number, id: number): number;
  gcs_dimension_pair_kind(ax: number, ay: number, bx: number, by: number,
                          px: number, py: number): number;
  gcs_fmt_g(v: number, sig: number): number;
  gcs_placed(x: number, px: number): number;

  gcs_branches_json(h: number): number;
  gcs_branches_set_json(h: number, p: number, n: number): void;
  gcs_without(h: number, ep: number, en: number, cp: number, cn: number): number;
  gcs_copy(h: number, ep: number, en: number): number;
  gcs_paste(h: number, clip: number, dx: number, dy: number): number;
  gcs_example(name: number, len: number): number;
  gcs_example_source(name: number, len: number): number;
  gcs_cases_json(): number;

  gcs_system_new(h: number): number;
  gcs_system_free(s: number): void;
  gcs_system_n_res(s: number): number;
  gcs_system_n_free(s: number): number;
  gcs_system_nnz(s: number): number;
  gcs_system_scale(s: number): number;
  gcs_system_hard(s: number, out: number): void;
  gcs_system_z0(s: number, h: number, out: number): void;
  gcs_system_residuals(s: number, z: number, out: number): void;
  gcs_system_jacobian_dense(s: number, z: number, out: number): void;
  gcs_system_csr_structure(s: number, indptr: number, indices: number): void;
  gcs_system_csr_data(s: number, z: number, out: number): void;
  gcs_system_max_hard_residual(s: number, h: number): number;
  gcs_system_constraint_errors(s: number, h: number, ids: number, out: number,
                               cap: number): number;
  gcs_system_n_constraints(s: number): number;
  gcs_system_max_relative_residual(s: number, h: number): number;
  gcs_system_rank(s: number, h: number, tol: number, hardOnly: number): number;
  gcs_system_conditioned(s: number, h: number, out: number): number;
  gcs_rank_tol(): number;
  gcs_system_update_consts(s: number, h: number, id: number): void;
  gcs_system_refresh_consts(s: number, h: number): void;
  gcs_system_structure_json(s: number): number;
  gcs_system_free_indices(s: number, out: number): void;
  gcs_system_row_of(s: number, id: number): number;
  gcs_system_solve(s: number, h: number, method: number, tol: number, maxIter: number,
                   maxNfev: number, dense: number, writeback: number, out: number): number;
  gcs_solve(h: number, method: number, tol: number, maxIter: number, maxNfev: number,
            dense: number, out: number): number;
  gcs_status_message(status: number): number;

  gcs_min_norm_lstsq(m: number, n: number, nrhs: number, a: number, b: number, rcond: number, x: number): number;
  gcs_rrqr(m: number, n: number, a: number, rcond: number, piv: number): number;
  gcs_svd(m: number, n: number, a: number, u: number, s: number, vt: number): number;
  gcs_rank_nullspace(m: number, n: number, a: number, rcond: number, nOut: number, sOut: number): number;
  gcs_lu_solve(n: number, a: number, b: number): number;

  gcs_check_sketch(h: number, rtol: number, atol: number): number;
  gcs_check_constraint(h: number, id: number, rtol: number, atol: number): number;

  gcs_hopcroft_karp_json(adj: number, len: number, nRight: number): number;
  gcs_dulmage_mendelsohn_json(adj: number, len: number, nCols: number): number;
  gcs_pebble_game_json(n: number, edges: number, len: number): number;
  gcs_bipartite_components_json(adj: number, len: number, nCols: number): number;
  gcs_henneberg_edges_json(n: number, seed: number): number;

  gcs_diagnose_json(h: number, p: number, n: number): number;
  gcs_diagnose_with_json(h: number, s: number, p: number, n: number): number;
  gcs_minimal_conflict_set_json(h: number, p: number, n: number, tol: number): number;
  gcs_violated_json(h: number, tol: number): number;
  gcs_distance_rigidity_json(h: number): number;
  gcs_witness_json(h: number, seed: number): number;
  gcs_make_witness(h: number, seed: number, out: number): void;

  gcs_graph_json(h: number): number;
  gcs_plan_solver_new(h: number, sticky: number): number;
  gcs_plan_solver_free(p: number): void;
  gcs_plan_solver_system(p: number): number;
  gcs_plan_solver_plan_json(p: number): number;
  gcs_plan_solver_graph_json(p: number): number;
  gcs_plan_solver_solve(p: number, h: number, tol: number, fallback: number, method: number): number;
  gcs_plan_solver_flip(p: number, h: number, point: number): number;
  gcs_plan_solver_sticky(p: number, sticky: number): void;
  gcs_plan_solver_execute(p: number, h: number): void;
  gcs_plan_solver_point_element(p: number, point: number): number;
  gcs_ppp_triangles(p: number, out: number): number;
  gcs_plan_steps_placing(p: number, point: number, out: number): number;

  gcs_enumerate_step_json(p: number, h: number, step: number, locate: number, seed: number, maxPaths: number): number;
  gcs_apply_alternative(p: number, h: number, step: number, alt: number, len: number): void;

  gcs_drag_new(h: number, point: number, x: number, y: number, method: number, weight: number,
               guards: number, nGuards: number, maxStepRel: number): number;
  gcs_drag_move(d: number, h: number, x: number, y: number, out: number): number;
  gcs_drag_end(d: number, h: number): void;
  gcs_drag_free(d: number): void;
  gcs_drag_flips(d: number): number;
  gcs_drag_flip_list(d: number, out: number): number;
  gcs_radius_drag_new(h: number, kind: number, idx: number, r: number, method: number): number;
  gcs_radius_drag_move(d: number, h: number, r: number, out: number): number;
  gcs_radius_drag_end(d: number, h: number): void;
  gcs_radius_drag_free(d: number): void;
  gcs_turn_drag_new(h: number, body: number, len: number): number;
  gcs_turn_drag_plane(d: number): number;
  gcs_turn_drag_carries(d: number): number;
  gcs_turn_drag_sweep(d: number, h: number, fx: number, fy: number, tx: number, ty: number,
                      out: number): number;
  gcs_turn_drag_seen(d: number, h: number, az: number, el: number, across: number, down: number,
                     out: number): number;
  gcs_turn_drag_free(d: number): void;
  gcs_plan_drag_new(h: number, ps: number, point: number, x: number, y: number, guards: number,
                    nGuards: number, maxStepRel: number): number;
  gcs_plan_drag_seen(h: number, ps: number, point: number, x: number, y: number, az: number,
                     el: number, maxStepRel: number): number;
  gcs_plan_drag_move(d: number, h: number, x: number, y: number, out: number): number;
  gcs_plan_drag_usable(d: number): number;
  gcs_plan_drag_flips(d: number): number;
  gcs_plan_drag_flip_list(d: number, out: number): number;
  gcs_plan_drag_branches_json(d: number): number;
  gcs_plan_drag_guards(d: number, h: number, out: number): number;
  gcs_plan_drag_end(d: number, h: number): void;
  gcs_plan_drag_free(d: number): void;
  gcs_parameters_json(h: number): number;
  gcs_parameter_json(h: number, np: number, nn: number): number;
  gcs_parameter_stand(h: number, np: number, nn: number, t: number): number;
  gcs_parameter_home(h: number): void;
  gcs_parameter_instant(h: number, np: number, nn: number, tp: number, tn: number): number;
  gcs_parameter_reading(h: number, np: number, nn: number, v: number): number;
  gcs_scrub_new(h: number, np: number, nn: number, point: number, seen: number, az: number,
                el: number): number;
  gcs_scrub_move(s: number, h: number, x: number, y: number): number;
  gcs_scrub_free(s: number): void;
}

let abi: Abi | null = null;
const hooks: (() => void)[] = [];

/** Run `h` once the core is loaded (immediately if it already is).  The constraint classes are
 *  generated from the core's registry, so they register here rather than at module load. */
export function onInit(h: () => void): void {
  if (abi) h();
  else hooks.push(h);
}

/** What a loading core says of itself: the bytes of the module arrived so far, of `total`.  The
 *  words are the page's; the last call, with every byte in, is the core starting. */
export type CoreProgress = (received: number, total: number) => void;

/** The module's size in bytes, written into the page's bundle when it is built
 *  (`tools/bundle.js`).  A host that compresses the module sends the compressed size as its
 *  Content-Length, while the body yields the module itself, so the header cannot say how much is
 *  left; unbundled (the test suite) there is no such constant. */
declare const GCS_WASM_BYTES: number | undefined;
/** The module's digest (a SHA-256's first sixteen hex digits), written in at bundling as its size
 *  is: what a build kept between openings is kept under (`surface-store.ts`), so a new core never
 *  reads an old core's.  Unbundled there is none, and nothing is kept. */
declare const GCS_WASM_HASH: string | undefined;

/** The core's build identity (`GCS_WASM_HASH`), or nothing where the page was not bundled. */
export function coreIdentity(): string | undefined {
  return typeof GCS_WASM_HASH === 'string' ? GCS_WASM_HASH : undefined;
}

const compile = async (bytes: BufferSource): Promise<WebAssembly.Instance> =>
  (await WebAssembly.instantiate(bytes, {})).instance;

/** Instantiate the module a response carries.  The module is **compiled as it arrives**
 *  (`instantiateStreaming`), so its compilation overlaps the download rather than following it,
 *  and a browser may keep the compiled code for the next visit; a host that does not say
 *  `application/wasm` gets the bytes compiled whole.  Given the module's `size`, `progress` hears
 *  the bytes arrive, counted on a clone of the body so the compiler reads the original. */
export async function fromResponse(res: Response, progress?: CoreProgress,
                                   size?: number): Promise<WebAssembly.Instance> {
  const counted = progress && size && res.body ? count(res.clone(), progress, size) : undefined;
  const streaming = typeof WebAssembly.instantiateStreaming === 'function'
    && /^application\/wasm\b/.test(res.headers.get('content-type') ?? '');
  const made = streaming ? WebAssembly.instantiateStreaming(res, {}).then((m) => m.instance)
    : res.arrayBuffer().then(compile);
  const [instance] = await Promise.all([made, counted]);
  return instance;
}

/** Read a response's body to its end, saying how much of it has arrived. */
async function count(res: Response, progress: CoreProgress, total: number): Promise<void> {
  const reader = res.body!.getReader();
  let got = 0;
  progress(0, total);
  for (let read = await reader.read(); !read.done; read = await reader.read()) {
    got += read.value.byteLength;
    progress(Math.min(got, total), total);
  }
  progress(total, total);
}

async function instantiate(progress?: CoreProgress): Promise<WebAssembly.Instance> {
  const target = new URL('../wasm/gcs.wasm', import.meta.url).href;
  if (typeof fetch === 'function' && !target.startsWith('file:')) {
    // the page asks for this same address in a preload, so the response is usually on its way
    // before this module has even run
    const res = await fetch(target);
    if (!res.ok) throw new Error(`could not fetch ${target}: ${res.status}`);
    return fromResponse(res, progress,
                        typeof GCS_WASM_BYTES === 'number' ? GCS_WASM_BYTES : undefined);
  }
  const { readFile } = await import('node:fs/promises');
  const { fileURLToPath } = await import('node:url');
  return compile(await readFile(fileURLToPath(target)));
}

/** Load the core once; every later call returns the same instance.  `progress` hears it arrive
 *  (`fromResponse`), where it is fetched. */
export async function initCore(opts?: { progress?: CoreProgress }): Promise<Abi> {
  if (abi) return abi;
  abi = (await instantiate(opts?.progress)).exports as unknown as Abi;
  for (const h of hooks.splice(0)) h();
  return abi;
}

/** The loaded core.  Throws if `initCore` has not resolved yet. */
export function core(): Abi {
  if (!abi) throw new Error('gcs core not initialised — await initCore() first');
  return abi;
}

/* -- heap access ------------------------------------------------------------ */

export const u8 = (): Uint8Array => new Uint8Array(core().memory.buffer);
export const i32 = (): Int32Array => new Int32Array(core().memory.buffer);
export const f64 = (): Float64Array => new Float64Array(core().memory.buffer);

/** A block of the core's heap with a matching view on demand. */
/** A scratch block in the core's heap.
 *
 *  The typed-array getters below build a *view* over the module's memory.  That memory grows on
 *  any core call, and growing detaches every view over the old buffer — so a view must never be
 *  held across a call into the core.  Read it, or copy out of it, first. */
export class Buf {
  readonly ptr: number;
  readonly bytes: number;

  constructor(readonly len: number, readonly width: 4 | 8 | 1 = 8) {
    this.bytes = Math.max(len * width, 8);
    this.ptr = core().gcs_malloc(this.bytes);
  }

  get f64(): Float64Array {
    return f64().subarray(this.ptr >> 3, (this.ptr >> 3) + this.len);
  }

  get i32(): Int32Array {
    return i32().subarray(this.ptr >> 2, (this.ptr >> 2) + this.len);
  }

  get u8(): Uint8Array {
    return u8().subarray(this.ptr, this.ptr + this.len);
  }

  set(src: ArrayLike<number>): this {
    if (this.width === 8) this.f64.set(src);
    else if (this.width === 4) this.i32.set(src);
    else this.u8.set(src);
    return this;
  }

  release(): void {
    core().gcs_free(this.ptr, this.bytes);
  }
}

/** Run `fn` with a scratch buffer, releasing it whatever happens. */
export function withBuf<T>(len: number, width: 4 | 8 | 1, fn: (b: Buf) => T): T {
  const b = new Buf(Math.max(len, 1), width);
  try {
    return fn(b);
  } finally {
    b.release();
  }
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Copy a string into the core's heap and hand `fn` its (pointer, length). */
export function withStr<T>(s: string, fn: (ptr: number, len: number) => T): T {
  const bytes = encoder.encode(s);
  const b = new Buf(Math.max(bytes.length, 1), 1);
  try {
    u8().set(bytes, b.ptr);
    return fn(b.ptr, bytes.length);
  } finally {
    b.release();
  }
}

export function withJson<T>(v: unknown, fn: (ptr: number, len: number) => T): T {
  return withStr(JSON.stringify(v), fn);
}

/** Consume a length-prefixed string block returned by the core. */
export function takeStr(handle: number): string {
  if (!handle) return '';
  const c = core();
  const n = c.gcs_str_len(handle);
  const p = c.gcs_str_ptr(handle);
  const out = n ? decoder.decode(u8().subarray(p, p + n)) : '';
  c.gcs_str_free(handle);
  return out;
}

/** Bytes out of the core, under the same handle a string uses — the convention was always
 *  bytes, and only this side assumed they were characters.  What a `.glb` comes back as. */
export function takeBytes(handle: number): Uint8Array {
  if (!handle) return new Uint8Array(0);
  const c = core();
  const n = c.gcs_str_len(handle);
  const p = c.gcs_str_ptr(handle);
  const out = new Uint8Array(u8().subarray(p, p + n));   // copied: the heap may move
  c.gcs_str_free(handle);
  return out;
}

export function takeJson<T>(handle: number): T {
  const s = takeStr(handle);
  return (s ? JSON.parse(s) : null) as T;
}

export function lastError(): string {
  return takeStr(core().gcs_last_error());
}
